import { useState } from "react";
import { Link } from "react-router-dom";
import { ProviderWithProducts } from "../../../../hooks/useProviders";
import { Product } from "../../../../lib/supabase";

interface Props {
  provider: ProviderWithProducts;
}

function ProductCard({ product, idx }: { product: Product; idx: number }) {
  const [hovered, setHovered] = useState(false);
  const isVideo = product.type === "video";
  const href = isVideo ? `/courses/${product.id}` : `/materials/${product.id}`;
  const thumbnail = product.thumbnail_url ||
    (isVideo
      ? `https://public.readdy.ai/ai/img_res/03f34fd2859e76675ea1439ae26b100e.jpg${product.id.slice(0, 8)}&orientation=landscape`
      : `https://readdy.ai/api/search-image?query=Japanese%20traditional%20craft%20material%20kit%20elegant%20product%20photography%20white%20background%20clean%20minimal%20high%20quality&width=400&height=240&seq=kit-th-${product.id.slice(0, 8)}&orientation=landscape`
    );

  return (
    <Link
      to={href}
      className="group block bg-white rounded-2xl overflow-hidden border border-stone-100 transition-all duration-300 cursor-pointer"
      style={{ transform: hovered ? "translateY(-4px)" : "translateY(0)", transition: "transform 0.28s ease" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative h-44 overflow-hidden">
        <img
          src={thumbnail}
          alt={product.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        {/* Type badge */}
        <div className="absolute top-3 left-3">
          <span className={`text-xs px-2.5 py-1 rounded-full font-sans font-medium ${isVideo ? "bg-teal text-white" : "bg-gold text-white"}`}>
            {isVideo ? "動画講座" : "教材キット"}
          </span>
        </div>
        {/* Free sample */}
        {product.is_free_sample && (
          <div className="absolute top-3 right-8">
            <span className="text-xs px-2.5 py-1 rounded-full font-sans font-medium bg-white/90 text-deep-green">
              無料サンプルあり
            </span>
          </div>
        )}
        {/* Play icon */}
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
              <i className="ri-play-fill text-deep-green text-xl ml-1" />
            </div>
          </div>
        )}
        {/* Index */}
        <div className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center">
          <span className="text-white/60 font-display italic text-sm">{String(idx + 1).padStart(2, "0")}</span>
        </div>
      </div>
      <div className="p-4">
        <h4 className="font-serif text-deep-green text-sm md:text-base font-medium leading-snug mb-2 group-hover:text-teal transition-colors line-clamp-2">
          {product.title}
        </h4>
        {product.difficulty && (
          <span className="text-xs text-charcoal/40 font-sans mb-2 block">{product.difficulty}</span>
        )}
        <div className="flex items-center justify-between">
          <p className="font-sans text-teal text-base font-medium">
            ¥{product.price.toLocaleString()}<span className="text-xs text-charcoal/40 font-light">（税込）</span>
          </p>
          <span className="text-xs text-teal font-sans">詳細 →</span>
        </div>
        {isVideo && product.total_minutes && (
          <p className="text-xs text-charcoal/40 font-sans mt-1 flex items-center gap-1">
            <i className="ri-time-line" />
            {product.total_minutes}分
          </p>
        )}
        {!isVideo && product.stock_count !== undefined && (
          <p className={`text-xs font-sans mt-1 flex items-center gap-1 ${product.stock_count > 0 ? "text-charcoal/40" : "text-red-400"}`}>
            <i className="ri-store-line" />
            {product.stock_count > 0 ? `在庫 ${product.stock_count}点` : "在庫切れ"}
          </p>
        )}
      </div>
    </Link>
  );
}

export default function ProviderProductsSection({ provider }: Props) {
  const videos = (provider.products || []).filter((p) => p.type === "video");
  const kits = (provider.products || []).filter((p) => p.type === "kit");

  return (
    <>
      {/* Video courses */}
      <section id="videos" className="py-14 px-4 md:px-0 border-t border-stone-100">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <p className="text-gold text-xs font-display italic tracking-widest mb-1">Video Courses</p>
            <h2 className="font-serif text-deep-green text-2xl font-light">
              動画講座
              <span className="text-base text-charcoal/40 font-sans font-normal ml-2">{videos.length}本</span>
            </h2>
          </div>
          {videos.some((v) => v.is_free_sample) && (
            <div className="flex items-center gap-2 text-xs font-sans text-teal">
              <i className="ri-play-circle-line" />
              一部無料サンプルあり
            </div>
          )}
        </div>

        {videos.length === 0 ? (
          <div className="text-center py-16 text-charcoal/40 font-sans bg-white/60 rounded-2xl border border-stone-100">
            <i className="ri-video-line text-4xl block mb-3 opacity-30" />
            動画講座は準備中です。フォローして最新情報をお受け取りください。
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {videos.map((p, i) => (
              <ProductCard key={p.id} product={p} idx={i} />
            ))}
          </div>
        )}
      </section>

      {/* Material kits */}
      <section id="kits" className="py-14 px-4 md:px-0 border-t border-stone-100">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <p className="text-gold text-xs font-display italic tracking-widest mb-1">Material Kits</p>
            <h2 className="font-serif text-deep-green text-2xl font-light">
              教材キット
              <span className="text-base text-charcoal/40 font-sans font-normal ml-2">{kits.length}点</span>
            </h2>
          </div>
        </div>

        {kits.length === 0 ? (
          <div className="text-center py-16 text-charcoal/40 font-sans bg-white/60 rounded-2xl border border-stone-100">
            <i className="ri-box-3-line text-4xl block mb-3 opacity-30" />
            教材キットは準備中です。フォローして最新情報をお受け取りください。
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {kits.map((p, i) => (
              <ProductCard key={p.id} product={p} idx={i} />
            ))}
          </div>
        )}

        {/* Purchase CTA */}
        <div className="mt-10 bg-deep-green/5 rounded-2xl p-6 flex flex-col md:flex-row items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-message-3-line text-teal" />
              <h3 className="font-serif text-deep-green text-base">購入後は講師に直接質問できます</h3>
            </div>
            <p className="text-sm text-charcoal/60 font-sans">
              動画講座・教材キットをご購入いただいたお客様は、{provider.name}に直接メッセージをお送りいただけます。
              学習上の疑問や実践での悩みに、講師本人が丁寧に回答します。
            </p>
          </div>
          <Link to="/login" className="btn-primary text-sm px-6 py-3 whitespace-nowrap">
            ログインして購入する →
          </Link>
        </div>
      </section>
    </>
  );
}
